import React, { useEffect, useRef, useState } from 'react';
import { ClipboardCheck, Send } from 'lucide-react';

export default function Reconciliation({ receiptId, version, status, api, active, disabled, canSubmit = false, canConfirm = false, onSubmit, onConfirm }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const generation = useRef(0), lock = useRef(false);
  useEffect(() => {
    generation.current += 1; setSummary(null); setError(''); setBusy(false); lock.current = false;
    return () => { generation.current += 1; };
  }, [disabled, receiptId, version, status]);
  async function review() {
    if (disabled || lock.current || !active()) return;
    const request = ++generation.current;
    const current = () => request === generation.current && active();
    lock.current = true; setBusy(true); setSummary(null); setError('');
    try {
      const result = await api.summary(receiptId);
      if (!current()) return;
      if (result.receipt_id !== receiptId || result.version !== version || result.status !== status) {
        throw new Error('Receipt changed or review version is unavailable. Reload the receipt before reviewing.');
      }
      setSummary(result);
    } catch (e) { if (current()) setError(e.message); }
    finally { if (current()) { lock.current = false; setBusy(false); } }
  }
  return <section className="receiving-arrival" aria-label="Receipt reconciliation">
    <h2>Reconciliation</h2>
    <button type="button" disabled={disabled || busy} onClick={review}><ClipboardCheck size={18} />Review saved receipt</button>
    {disabled && <p>Unsaved changes or a pending operation.</p>}
    {busy && <p role="status">Loading reconciliation...</p>}
    {error && <p role="alert" className="receiving-error">{error}</p>}
    {!disabled && summary && <>
      <p>Reviewed version: {summary.version}</p>
      <p role="status">{summary.complete ? 'All quantities recorded' : 'Missing quantities'} · {summary.requires_review ? 'Review required' : 'No discrepancies recorded'}</p>
      <p>Total value variance: {summary.value_variance} (PO currency)</p>
      <ul className="receiving-reconciliation">{summary.lines.map(line => <li key={line.line_index}>
        <h3>{line.description || line.ingredient_id || `Line ${line.line_index + 1}`}</h3>
        <dl>
          <dt>Expected / received</dt><dd>{line.expected_qty} / {line.received_qty ?? 'Not recorded'} {line.uom}</dd>
          <dt>Quantity variance</dt><dd>{line.qty_variance ?? 'Not calculated'} {line.uom}</dd>
          <dt>Value variance</dt><dd>{line.value_variance ?? 'Not calculated'}</dd>
          <dt>Temperature</dt><dd>{line.temp_ok === true ? 'Passed' : 'Not verified / outside limits'}</dd>
          <dt>Reason</dt><dd>{line.reason_code || 'Not recorded'}</dd>
          <dt>Evidence</dt><dd>{(line.evidence || []).length} attached</dd>
        </dl>
        {line.needs_review && <p className="receiving-error">Review required{!line.reason_code ? ' · Missing reason' : ''}{!line.evidence?.length ? ' · Missing evidence' : ''}</p>}
      </li>)}</ul>
      {canSubmit && <button type="button" disabled={!summary.complete || summary.lines.some(line => line.needs_review && (!line.reason_code || !line.evidence?.length))}
        onClick={() => { if (!disabled && active()) onSubmit(summary); }}><Send size={18} />Submit for review</button>}
      {canConfirm && <button type="button" disabled={!summary.complete || summary.requires_review}
        onClick={() => { if (!disabled && active()) onConfirm(summary); }}><ClipboardCheck size={18}/>Self-confirm exact delivery</button>}
    </>}
  </section>;
}
